import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { Sparkles } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";

import { cn } from "@/components/ui/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import * as m from "@/paraglide/messages";

type TabKey = "studyMaterials" | "flashcards" | "aiTutor" | "studentsLearn";

const CARD_KEYS: readonly TabKey[] = ["studyMaterials", "flashcards", "aiTutor", "studentsLearn"];

export function AdminSection() {
	const cards: Array<{ key: TabKey; label: string; content: ReactNode }> = [
		{
			key: "studyMaterials",
			label: m.landing_admin_tab1(),
			content: <StudyMaterialsCard />,
		},
		{
			key: "flashcards",
			label: m.landing_admin_tab2(),
			content: <FlashcardsCard />,
		},
		{
			key: "aiTutor",
			label: m.landing_admin_tab3(),
			content: <AITutorCard />,
		},
		{
			key: "studentsLearn",
			label: m.landing_admin_tab4(),
			content: <StudentTableCard />,
		},
	];
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [emblaRef, carouselApi] = useEmblaCarousel(
		{
			align: "start",
			loop: true,
		},
		[WheelGesturesPlugin({ forceWheelAxis: "x" })],
	);
	const active = cards[selectedIndex]?.key ?? cards[0].key;
	const activeTabId = `admin-feature-tab-${active}`;

	const syncSelectedIndex = useCallback(() => {
		if (!carouselApi) return;
		setSelectedIndex(carouselApi.selectedScrollSnap());
	}, [carouselApi]);

	useEffect(() => {
		if (!carouselApi) return;
		syncSelectedIndex();
		carouselApi.on("select", syncSelectedIndex);
		carouselApi.on("reInit", syncSelectedIndex);

		return () => {
			carouselApi.off("select", syncSelectedIndex);
			carouselApi.off("reInit", syncSelectedIndex);
		};
	}, [carouselApi, syncSelectedIndex]);

	const handleTabChange = useCallback(
		(value: string) => {
			const nextIndex = CARD_KEYS.indexOf(value as TabKey);
			if (nextIndex < 0) return;
			setSelectedIndex(nextIndex);
			carouselApi?.scrollTo(nextIndex);
		},
		[carouselApi],
	);

	return (
		<section className="px-4 py-20 md:px-12 md:py-24">
			<div className="mx-auto max-w-6xl">
				<h2 className="text-balance text-center text-4xl font-semibold leading-tight text-[#272727] md:text-5xl">
					{m.landing_admin_heading1()}
					<br />
					<span className="font-extrabold italic text-primary">
						{m.landing_admin_heading2()}
					</span>
				</h2>

				<Tabs
					value={active}
					onValueChange={handleTabChange}
					className="mt-7 items-center gap-0"
				>
					<TabsList
						aria-label="StudyOS feature cards"
						className="!h-auto min-h-14 max-w-full flex-nowrap justify-start gap-1 overflow-x-auto rounded-full bg-card/95 p-1.5 shadow-[0_6px_18px_rgba(0,0,0,0.08)] ring-1 ring-foreground/[0.04] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
					>
						{cards.map((tab) => (
							<TabsTrigger
								key={tab.key}
								id={`admin-feature-tab-${tab.key}`}
								value={tab.key}
								aria-controls="admin-feature-carousel-panel"
								className="h-11 flex-none rounded-full border-0 px-6 text-[15px] font-semibold leading-none text-[#434343] shadow-none after:hidden hover:bg-[#f3f3f3] hover:text-[#292929] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 data-active:bg-primary data-active:text-primary-foreground data-active:shadow-[0_7px_16px] data-active:shadow-primary/25 dark:data-active:bg-primary dark:data-active:text-primary-foreground"
							>
								{tab.label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>

			<div
				id="admin-feature-carousel-panel"
				ref={emblaRef}
				role="tabpanel"
				aria-labelledby={activeTabId}
				className="mt-10 overflow-hidden px-4 pb-6 md:px-12"
			>
				<div className="-ml-6 flex touch-pan-y">
					{cards.map((card) => (
						<FeatureCard key={card.key}>{card.content}</FeatureCard>
					))}
				</div>
			</div>
		</section>
	);
}

type FeatureCardProps = {
	children: ReactNode;
};

function FeatureCard({ children }: FeatureCardProps) {
	return <div className="ml-6 w-[min(92vw,900px)] min-w-0 shrink-0 grow-0">{children}</div>;
}

function StudyMaterialsCard() {
	return (
		<div className="relative flex flex-col gap-6 overflow-hidden rounded-[40px] bg-secondary p-8 md:h-[450px] md:flex-row md:items-center md:gap-[30px]">
			<div className="relative h-[400px] w-full overflow-hidden rounded-[24px] bg-[#fff7e1] md:h-full md:w-[500px] md:shrink-0 md:rounded-[30px]">
				<div className="pointer-events-none absolute -top-[109px] left-[44px] size-[572px] rounded-full bg-[radial-gradient(circle_at_center,_#fff_0%,_#ffe8a8_55%,_transparent_78%)] opacity-90" />

				<FileChip
					label="PDF"
					bgColor="#ffeaeb"
					accentColor="var(--destructive)"
					className="left-[78px] top-[55px]"
				/>
				<FileChip
					label="MP3"
					bgColor="#ffe1f3"
					accentColor="#c026a3"
					className="left-[15px] top-[140px]"
				/>
				<FileChip
					label="DOC"
					bgColor="#e6f4fd"
					accentColor="#1d6ae5"
					className="left-[78px] top-[225px]"
				/>

				<img
					src="/images/study-materials-arrow.svg"
					alt=""
					aria-hidden="true"
					className="pointer-events-none absolute left-24 top-32 h-20 w-32 z-10"
				/>

				<div className="pointer-events-none absolute left-[145px] top-1/2 flex h-[432px] w-[392px] -translate-y-1/2 items-center justify-center">
					<div className="-rotate-[14deg]">
						<div className="flex w-[313px] flex-col gap-6 rounded-2xl bg-card px-6 py-[60px] shadow-[0_0_14.8px_rgba(223,161,0,0.25)]">
							<FeatureRow
								tileColor="#d5ffd4"
								label={m.landing_admin_smNotes()}
								body={m.landing_admin_smNotesBody()}
								icon={<NoteGlyph />}
							/>
							<FeatureRow
								tileColor="#e6f4fd"
								label={m.landing_admin_smQuizzes()}
								body={m.landing_admin_smQuizzesBody()}
								icon={<DocGlyph accentColor="#1d6ae5" />}
							/>
							<FeatureRow
								tileColor="#f6f1ff"
								label={m.landing_admin_smFlashcards()}
								body={m.landing_admin_smFlashcardsBody()}
								icon={<FlashcardGlyph />}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-4">
				<h3 className="text-[32px] font-semibold leading-[1.05] text-[#3a3a3a]">
					{m.landing_admin_smTitle()}
				</h3>
				<p className="text-base leading-relaxed text-[#404040]">
					{m.landing_admin_smBody()}
				</p>
				<CtaButton>{m.landing_admin_cta()}</CtaButton>
			</div>
		</div>
	);
}

type FileChipProps = {
	label: "PDF" | "DOC" | "MP3";
	bgColor: string;
	accentColor: string;
	className?: string;
};

function FileChip({ label, bgColor, accentColor, className }: FileChipProps) {
	return (
		<div
			className={cn(
				"absolute flex size-[63px] items-center justify-center rounded-[22px] shadow-[0_4px_10px_rgba(0,0,0,0.06)]",
				className,
			)}
			style={{ backgroundColor: bgColor }}
		>
			<FileGlyph label={label} accentColor={accentColor} />
		</div>
	);
}

type FileGlyphProps = {
	label: "PDF" | "DOC" | "MP3";
	accentColor: string;
};

function FileGlyph({ label, accentColor }: FileGlyphProps) {
	return (
		<svg viewBox="0 0 32 40" className="h-[40px] w-[32px]" aria-hidden="true">
			<title>{`${label} file`}</title>
			<path
				d="M3 4 a3 3 0 0 1 3 -3 h14 l9 9 v26 a3 3 0 0 1 -3 3 H6 a3 3 0 0 1 -3 -3 z"
				fill={accentColor}
			/>
			<path d="M20 1 v6 a2 2 0 0 0 2 2 h7 z" fill="white" fillOpacity="0.35" />
			<rect x="5" y="22" width="22" height="11" rx="2" fill="white" fillOpacity="0.18" />
			<text
				x="16"
				y="30.5"
				fontSize="8.5"
				fontWeight="700"
				fill="white"
				textAnchor="middle"
				fontFamily="Inter, system-ui, sans-serif"
				letterSpacing="0.5"
			>
				{label}
			</text>
		</svg>
	);
}

function FeatureRow({
	tileColor,
	label,
	body,
	icon,
}: {
	tileColor: string;
	label: string;
	body: string;
	icon: ReactNode;
}) {
	return (
		<div className="flex items-center gap-4">
			<span
				className="flex size-[60px] shrink-0 items-center justify-center rounded-[14px]"
				style={{ backgroundColor: tileColor }}
			>
				{icon}
			</span>
			<div className="min-w-0">
				<p className="text-[18px] font-semibold leading-none text-[#5e5e5e]">{label}</p>
				<p className="mt-1 text-sm leading-snug text-[#7c7c7c]">{body}</p>
			</div>
		</div>
	);
}

function NoteGlyph() {
	return (
		<svg viewBox="0 0 32 40" className="h-[40px] w-[32px]" aria-hidden="true">
			<title>Note</title>
			<path
				d="M3 4 a3 3 0 0 1 3 -3 h14 l9 9 v26 a3 3 0 0 1 -3 3 H6 a3 3 0 0 1 -3 -3 z"
				fill="#1ea41b"
			/>
			<path d="M20 1 v6 a2 2 0 0 0 2 2 h7 z" fill="white" fillOpacity="0.35" />
			<rect x="5" y="22" width="22" height="11" rx="2" fill="white" fillOpacity="0.18" />
			<text
				x="16"
				y="30.5"
				fontSize="8.5"
				fontWeight="700"
				fill="white"
				textAnchor="middle"
				fontFamily="Inter, system-ui, sans-serif"
				letterSpacing="0.3"
			>
				Note
			</text>
		</svg>
	);
}

function DocGlyph({ accentColor }: { accentColor: string }) {
	return (
		<svg viewBox="0 0 32 40" className="h-[40px] w-[32px]" aria-hidden="true">
			<title>Document</title>
			<path
				d="M3 4 a3 3 0 0 1 3 -3 h14 l9 9 v26 a3 3 0 0 1 -3 3 H6 a3 3 0 0 1 -3 -3 z"
				fill={accentColor}
			/>
			<path d="M20 1 v6 a2 2 0 0 0 2 2 h7 z" fill="white" fillOpacity="0.35" />
			<circle cx="9" cy="22" r="1.4" fill="white" />
			<rect x="12" y="21" width="13" height="2" rx="1" fill="white" />
			<circle cx="9" cy="28" r="1.4" fill="white" />
			<rect x="12" y="27" width="13" height="2" rx="1" fill="white" />
		</svg>
	);
}

function FlashcardGlyph() {
	return (
		<div className="flex items-center">
			<span className="-mr-[7px] block h-[32px] w-[20px] -rotate-[16deg] rounded-[4px] bg-destructive shadow-[0_4px_5px_rgba(0,0,0,0.25)]" />
			<span className="block h-[32px] w-[20px] rounded-[4px] bg-[#ff8d85] shadow-[0_4px_5px_rgba(0,0,0,0.25)]" />
			<span className="-ml-[7px] block h-[32px] w-[20px] rotate-[12deg] rounded-[4px] bg-destructive shadow-[0_4px_5px_rgba(0,0,0,0.25)]" />
		</div>
	);
}

function FlashcardsCard() {
	return (
		<div className="relative flex flex-col gap-6 overflow-hidden rounded-[40px] bg-accent p-8 md:h-[450px] md:flex-row md:items-center md:gap-[30px]">
			<div className="relative h-[400px] w-full overflow-hidden rounded-[24px] bg-card md:h-full md:w-[500px] md:shrink-0 md:rounded-[30px]">
				<div className="pointer-events-none absolute left-[14px] top-[115px] size-[456px] rounded-full bg-[radial-gradient(circle_at_center,_#f4e3ff_0%,_#e9d3fb_50%,_transparent_75%)] opacity-90" />
				<div className="pointer-events-none absolute left-[101px] top-[201px] size-[283px] rounded-full bg-[radial-gradient(circle_at_center,_#e0c6f7_0%,_#cfa6f1_55%,_transparent_80%)] opacity-80" />

				<div className="absolute left-[18px] top-[calc(50%-65px)] -translate-y-1/2">
					<div className="relative -rotate-[7.74deg]">
						<div className="absolute -left-[6px] top-[8px] h-[156px] w-[300px] rounded-2xl bg-card/60 shadow-[0_0_29.6px_rgba(223,161,0,0.2)]" />
						<div className="relative flex h-[156px] w-[300px] flex-col gap-4 rounded-2xl bg-card p-4 shadow-[0_0_14.8px_rgba(223,161,0,0.25)]">
							<span className="inline-flex w-fit items-center justify-center rounded-full bg-[#d5ffd4] px-2 py-0.5 text-[15px] font-semibold leading-none text-[#1a8f5a]">
								{m.landing_admin_fcFront()}
							</span>
							<p className="text-[20px] font-semibold leading-none text-[#5e5e5e]">
								{m.landing_admin_fcQuestion()}
							</p>
						</div>
					</div>
				</div>

				<div className="absolute left-[116px] top-[calc(50%+30px)] -translate-y-1/2">
					<div className="relative rotate-[9.49deg]">
						<div className="absolute -left-[6px] top-[8px] h-[156px] w-[354px] rounded-2xl bg-primary/55 shadow-[0_0_29.6px_rgba(223,161,0,0.2)]" />
						<div className="relative flex h-[156px] w-[354px] flex-col gap-4 rounded-2xl bg-primary p-4 shadow-[0_0_14.8px_rgba(223,161,0,0.25)]">
							<span className="inline-flex w-fit items-center justify-center rounded-full bg-chart-4 px-2 py-0.5 text-[15px] font-semibold leading-none text-[#262ddf]">
								{m.landing_admin_fcBack()}
							</span>
							<p className="text-[20px] font-semibold leading-none text-primary-foreground">
								{m.landing_admin_fcAnswer()}
							</p>
							<p className="text-sm leading-snug text-primary-foreground">
								{m.landing_admin_fcExplain()}
							</p>
						</div>
					</div>
				</div>
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-4">
				<h3 className="text-[32px] font-semibold leading-[1.05] text-[#3a3a3a]">
					{m.landing_admin_fcTitle()}
				</h3>
				<p className="text-base leading-relaxed text-[#404040]">
					{m.landing_admin_fcBody()}
				</p>
				<CtaButton>{m.landing_admin_cta()}</CtaButton>
			</div>
		</div>
	);
}

function AITutorCard() {
	return (
		<div className="relative flex flex-col gap-6 overflow-hidden rounded-[40px] bg-chart-4 p-8 md:h-[450px] md:flex-row md:items-center md:gap-[30px]">
			<div className="relative h-[400px] w-full overflow-hidden rounded-[24px] bg-[#ecf8ff] md:h-full md:w-[500px] md:shrink-0 md:rounded-[30px]">
				<div className="pointer-events-none absolute -left-12 top-1/2 h-[590px] w-[710px] -translate-y-1/2 rounded-full bg-card" />

				<div
					aria-hidden="true"
					className="pointer-events-none absolute right-[20px] bottom-[20px] size-[125px] rotate-[10deg] rounded-[28px] opacity-90"
					style={{
						background:
							"radial-gradient(60% 60% at 30% 30%, #ffe082 0%, #ff8a65 35%, #ce93d8 70%, #7e57c2 100%)",
						boxShadow: "0 8px 24px rgba(126,87,194,0.35)",
					}}
				/>

				<div className="absolute left-[72px] top-[103px] flex size-[60px] items-center justify-center overflow-hidden rounded-2xl bg-[#faeeff]">
					<Sparkles className="size-6 text-[#a06ed4]" />
				</div>

				<div className="absolute left-[180px] top-[15px] flex w-[255px] flex-col rounded-2xl rounded-bl-md bg-card px-4 py-3 shadow-[0_4px_12px_rgba(0,0,0,0.06)]">
					<p className="text-[15px] font-semibold leading-snug text-[#5e5e5e]">
						{m.landing_admin_aiQuestion()}
					</p>
				</div>

				<p className="absolute left-[147px] top-[105px] w-[288px] text-[15px] leading-snug text-[#515151]">
					{m.landing_admin_aiAnswer()}
				</p>

				<div className="absolute left-[132px] top-[213px] flex h-[154px] w-[288px] flex-col gap-2 overflow-hidden rounded-md bg-gradient-to-br from-[#bce5a3] via-[#83c87e] to-[#3a8c52] p-3 shadow-[0_4px_12px_rgba(58,140,82,0.25)]">
					<div className="flex items-center gap-2 rounded-md bg-card/70 px-2 py-1 text-[11px] font-semibold text-[#256032]">
						<span className="inline-block size-2 rounded-full bg-[#256032]" />
						{m.landing_admin_aiDiagramLabel()}
					</div>
					<div className="relative flex flex-1 items-end justify-center">
						<svg viewBox="0 0 120 70" className="h-full w-full" aria-hidden="true">
							<title>Diagram</title>
							<circle cx="100" cy="12" r="8" fill="#fde68a" />
							<path
								d="M100 22 L60 38"
								stroke="#fff7c2"
								strokeWidth="1.5"
								strokeLinecap="round"
							/>
							<path
								d="M60 60 C 40 50, 40 36, 60 30 C 80 36, 80 50, 60 60 Z"
								fill="#256032"
							/>
							<path
								d="M60 60 L 60 70"
								stroke="#5c3a1d"
								strokeWidth="2"
								strokeLinecap="round"
							/>
							<path
								d="M30 70 L 90 70"
								stroke="#7c4a26"
								strokeWidth="2"
								strokeLinecap="round"
							/>
						</svg>
					</div>
				</div>
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-4">
				<h3 className="text-[32px] font-semibold leading-[1.05] text-[#3a3a3a]">
					{m.landing_admin_aiTitle()}
				</h3>
				<p className="text-base leading-relaxed text-[#404040]">
					{m.landing_admin_aiBody()}
				</p>
				<CtaButton>{m.landing_admin_cta()}</CtaButton>
			</div>
		</div>
	);
}

const STUDENT_ROWS = [
	{
		id: "ami-smith",
		name: "Ami Smith",
		email: "ami.smith@studyos.com",
		sessions: 24,
		rate: "100%",
	},
	{
		id: "coco-liya-1",
		name: "Coco Liya",
		email: "coco.liya@studyos.com",
		sessions: 18,
		rate: "75%",
	},
	{ id: "adam-ho", name: "Adam Ho", email: "adam.ho@studyos.com", sessions: 24, rate: "100%" },
	{
		id: "coco-liya-2",
		name: "Coco Liya",
		email: "coco.liya@studyos.com",
		sessions: 18,
		rate: "75%",
	},
] as const;

function StudentTableCard() {
	return (
		<div className="relative flex flex-col gap-6 overflow-hidden rounded-[40px] bg-primary p-8 text-primary-foreground md:h-[450px] md:flex-row md:items-center md:gap-[30px]">
			<div className="relative h-[400px] w-full overflow-hidden rounded-[24px] bg-[#e3f4ff] md:h-full md:w-[500px] md:shrink-0 md:rounded-[30px]">
				<div className="pointer-events-none absolute -left-7 top-0 h-[511px] w-[531px] rounded-full bg-[radial-gradient(circle_at_30%_40%,_#bfe3ff_0%,_#88c2ec_55%,_transparent_80%)] opacity-90" />
				<div className="pointer-events-none absolute -bottom-9 -left-7 size-[283px] rounded-full bg-[radial-gradient(circle_at_center,_#8ab9e3_0%,_#5b8fc4_55%,_transparent_80%)] opacity-80" />

				<div className="absolute left-1/2 top-1/2 w-[447px] -translate-x-1/2 -translate-y-1/2">
					<div className="-rotate-[7deg] space-y-2">
						<div className="flex items-center gap-1 rounded-lg bg-[#fdda6d] px-4 py-2 text-[15px] text-foreground shadow-[0_4px_4.75px_rgba(0,0,0,0.18)]">
							<p className="w-[167px] font-medium">{m.landing_admin_stColName()}</p>
							<p className="w-[64px] font-medium">
								{m.landing_admin_stColSessions()}
							</p>
							<p className="w-[76px] font-medium">{m.landing_admin_stColRate()}</p>
							<p className="font-medium whitespace-nowrap">
								{m.landing_admin_stColDate()}
							</p>
						</div>
						{STUDENT_ROWS.map((row, i) => (
							<div
								key={row.id}
								className={cn(
									"flex items-center gap-1 rounded-lg px-4 py-2 shadow-[0_4px_4.75px_rgba(0,0,0,0.18)]",
									i % 2 === 0 ? "bg-[#e2f4ff]" : "bg-card",
								)}
							>
								<div className="flex w-[167px] min-w-0 flex-col">
									<p className="truncate text-base text-foreground">{row.name}</p>
									<p className="truncate text-sm text-[#727272]">{row.email}</p>
								</div>
								<p className="w-[64px] text-base text-foreground">{row.sessions}</p>
								<p className="w-[76px] text-base text-foreground">{row.rate}</p>
								<p className="text-base text-foreground whitespace-nowrap">
									{m.landing_admin_stDayAgo()}
								</p>
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-4">
				<h3 className="text-[32px] font-semibold leading-[1.05] text-primary-foreground">
					{m.landing_admin_stTitle()}
				</h3>
				<p className="text-base leading-relaxed text-[#f4f4f4]">
					{m.landing_admin_stBody()}
				</p>
				<CtaButton>{m.landing_admin_cta()}</CtaButton>
			</div>
		</div>
	);
}

function CtaButton({ children }: { children: ReactNode }) {
	return (
		<button
			type="button"
			className="mt-2 inline-flex w-fit items-center rounded-full bg-[#fff7f0] px-6 py-3 text-base font-bold text-primary transition-shadow hover:shadow-md"
		>
			{children}
		</button>
	);
}
